/* Honeybear Coffee | compact luxury product v4
   Progressive enhancement, native Shopify form, server-rendered option choices.
   This file is scoped to [data-hbp3-product] and safe in Shopify Theme Editor. */
(() => {
  'use strict';
  const all = (root, selector) => Array.from(root.querySelectorAll(selector));
  const routes = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const num = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  function init(root) {
    if (root.dataset.hbp3Ready === '1') return;
    root.dataset.hbp3Ready = '1';
    let pending = null;
    let seq = 0;
    let sending = false;
    let toastTimer;
    let mobileObserver;
    const mobile = root.querySelector('[data-hbp3-mobile]');
    const formSelector = 'form.hbp3-form';

    function activateForm() {
      const form = root.querySelector(formSelector);
      if (!form) return;
      const native = form.querySelector('[data-hbp3-native]');
      const hidden = form.querySelector('[data-hbp3-id]');
      // No-JS visitors keep the native Shopify variant select.
      if (native && hidden) {
        hidden.name = 'id';
        native.disabled = true;
      }
      form.dataset.jsReady = 'true';
    }

    // Native scroll-snap track: arrows, thumbnails, touch swipes and variants sync.
    // Only the image strip scrolls; the whole page never jumps on thumbnail click.
    const viewport = root.querySelector('[data-hbp3-viewport]');
    let activeSlide = 0;
    let slideScrollFrame = 0;
    let galleryInitialized = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    function slides() { return all(root, '[data-hbp3-slide]'); }
    function currentSlideIndex() { return activeSlide; }
    function syncSlide(index) {
      const list = slides();
      if (!list.length) return;
      activeSlide = Math.max(0, Math.min(list.length - 1, index));
      list.forEach((slide, i) => {
        const isActive = i === activeSlide;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.inert = !isActive;
        if (!isActive) all(slide, 'video').forEach((video) => {
          try { video.pause(); } catch (_) { /* media not ready */ }
        });
      });
      const thumbs = all(root, '[data-hbp3-thumb]');
      thumbs.forEach((thumb) => {
        const selected = thumb.dataset.mediaId === list[activeSlide].dataset.mediaId;
        thumb.classList.toggle('is-current', selected);
        thumb.setAttribute('aria-pressed', String(selected));
      });
      const strip = root.querySelector('.hbp3-thumbs');
      const selectedThumb = thumbs.find((thumb) => thumb.dataset.mediaId === list[activeSlide].dataset.mediaId);
      if (strip && selectedThumb) {
        const stripBounds = strip.getBoundingClientRect();
        const thumbBounds = selectedThumb.getBoundingClientRect();
        if (thumbBounds.left < stripBounds.left || thumbBounds.right > stripBounds.right) {
          const shift = thumbBounds.left - stripBounds.left - (strip.clientWidth - selectedThumb.clientWidth) / 2;
          strip.scrollTo({left: strip.scrollLeft + shift, behavior: reducedMotion.matches ? 'auto' : 'smooth'});
        }
      }
      const counter = root.querySelector('[data-hbp3-counter]');
      if (counter) counter.textContent = `${activeSlide + 1} / ${list.length}`;
    }
    function showSlide(idOrIndex, asIndex = false, immediate = false) {
      const list = slides();
      if (!list.length) return;
      let index = asIndex ? Number(idOrIndex) : list.findIndex((slide) => slide.dataset.mediaId === String(idOrIndex));
      if (!Number.isFinite(index) || index < 0 || index >= list.length) index = 0;
      syncSlide(index);
      if (viewport && galleryInitialized) {
        viewport.scrollTo({left: list[index].offsetLeft, behavior: immediate || reducedMotion.matches ? 'auto' : 'smooth'});
      }
    }
    function initGallery() {
      if (!viewport) return;
      const list = slides();
      if (!list.length) return;
      const initialIndex = Math.max(0, list.findIndex((slide) => !slide.hidden));
      // Keep featured image rendered server-side for visitors without JavaScript.
      // Once JS is ready, expose every slide to the horizontal scroll track.
      list.forEach((slide) => {slide.hidden = false;});
      galleryInitialized = true;
      viewport.scrollLeft = list[initialIndex].offsetLeft;
      syncSlide(initialIndex);
      viewport.addEventListener('scroll', () => {
        if (slideScrollFrame) cancelAnimationFrame(slideScrollFrame);
        slideScrollFrame = requestAnimationFrame(() => {
          slideScrollFrame = 0;
          const nearest = slides().reduce((best, slide, i, arr) =>
            Math.abs(slide.offsetLeft - viewport.scrollLeft) < Math.abs(arr[best].offsetLeft - viewport.scrollLeft) ? i : best, 0);
          if (nearest !== activeSlide) syncSlide(nearest);
        });
      }, {passive:true});
      viewport.addEventListener('keydown', (event) => {
        if (event.target !== viewport) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        showSlide((activeSlide + (event.key === 'ArrowRight' ? 1 : -1) + slides().length) % slides().length, true);
      });
    }

    function validQty(field) {
      const min = Math.max(1, num(field.min, 1));
      const step = Math.max(1, num(field.step, 1));
      const max = field.max ? num(field.max, Infinity) : Infinity;
      let value = Math.max(min, Math.min(max, num(field.value, min)));
      value = min + Math.floor((value - min) / step) * step;
      field.value = String(Math.max(min, Math.min(max, value)));
      return {min, step, max};
    }
    function flash(message) {
      const note = root.querySelector('[data-hbp3-toast]');
      if (!note) return;
      const label = note.querySelector('[data-hbp3-toast-text]');
      if (label) label.textContent = message;
      note.hidden = false;
      clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => {note.hidden = true;}, 5200);
    }
    function updateMobile() {
      if (!mobile) return;
      const price = root.querySelector('.hbp3-price');
      const submit = root.querySelector('[data-hbp3-submit]');
      const mobilePrice = mobile.querySelector('[data-hbp3-mobile-price]');
      const mobileAction = mobile.querySelector('[data-hbp3-mobile-action]');
      if (price && mobilePrice) mobilePrice.textContent = price.textContent.trim();
      if (submit && mobileAction) {
        mobileAction.disabled = submit.disabled;
        const mobileText = mobile.querySelector('[data-hbp3-mobile-text]');
        if (mobileText) mobileText.textContent = submit.disabled ? 'SOLD OUT' : 'ADD TO BAG';
      }
    }
    function observeMobileButton() {
      if (!mobile) return;
      mobileObserver?.disconnect();
      const main = root.querySelector('[data-hbp3-submit]');
      if (!main || typeof IntersectionObserver === 'undefined') return;
      mobileObserver = new IntersectionObserver(([entry]) => {
        const below = entry.boundingClientRect.bottom < 0;
        mobile.hidden = !(window.matchMedia('(max-width: 720px)').matches && !entry.isIntersecting && below && window.scrollY > 180);
      }, {threshold: 0});
      mobileObserver.observe(main);
    }

    // Native controls: thumbnails, quantity, lightbox, mobile purchase.
    root.addEventListener('click', (event) => {
      const target = event.target;
      const thumb = target.closest('[data-hbp3-thumb]');
      if (thumb && root.contains(thumb)) {showSlide(thumb.dataset.mediaId); return;}
      const prev = target.closest('[data-hbp3-prev]');
      const next = target.closest('[data-hbp3-next]');
      if (prev || next) {
        const list = slides();
        if (list.length > 1) showSlide((currentSlideIndex() + (next ? 1 : -1) + list.length) % list.length, true);
        return;
      }
      const plus = target.closest('[data-hbp3-plus]');
      const minus = target.closest('[data-hbp3-minus]');
      if (plus || minus) {
        const quantity = root.querySelector('[data-hbp3-qty]');
        if (!quantity) return;
        const {min, step, max} = validQty(quantity);
        quantity.value = String(Math.max(min, Math.min(max, num(quantity.value, min) + (plus ? step : -step))));
        return;
      }
      if (target.closest('[data-hbp3-toast-close]')) root.querySelector('[data-hbp3-toast]').hidden = true;
      if (target.closest('[data-hbp3-mobile-action]')) {
        const btn = root.querySelector('[data-hbp3-submit]');
        const form = root.querySelector(formSelector);
        if (btn && !btn.disabled) {
          if (form?.requestSubmit) form.requestSubmit(btn);
          else btn.click();
        }
      }
    });

    // Shopify Section Rendering API keeps true server-rendered variant prices,
    // availability, option-values and subscription price in sync.
    async function updateOptions(changed) {
      const productURL = new URL(root.dataset.productUrl, window.location.origin);
      if (changed?.dataset.relatedProduct) {
        const redirect = new URL(changed.dataset.relatedProduct, window.location.origin);
        if (redirect.pathname !== productURL.pathname) {
          window.location.assign(redirect.toString());
          return;
        }
      }
      const values = all(root, '[data-hbp3-option-id]:checked').map((node) => node.dataset.hbp3OptionId).filter(Boolean);
      const plan = root.querySelector('[data-hbp3-plan]')?.value || '';
      const url = new URL(productURL);
      if (values.length) url.searchParams.set('option_values', values.join(','));
      if (plan) url.searchParams.set('selling_plan', plan);
      const request = new URL(url);
      request.searchParams.set('section_id', root.dataset.sectionId);
      const savedQty = root.querySelector('[data-hbp3-qty]')?.value || '1';
      const expanded = all(root, '.hbp3-details').map((node) => node.open);
      pending?.abort();
      pending = new AbortController();
      const id = ++seq;
      root.setAttribute('aria-busy', 'true');
      root.querySelector('[data-hbp3-submit]')?.setAttribute('disabled', 'disabled');
      try {
        const response = await fetch(request.toString(), {signal:pending.signal, headers:{Accept:'text/html'}});
        if (!response.ok) throw Error('Could not update product options');
        const documentHTML = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = documentHTML.querySelector('[data-hbp3-product]');
        const newInfo = incoming?.querySelector('[data-hbp3-info]');
        const oldInfo = root.querySelector('[data-hbp3-info]');
        if (!newInfo || !oldInfo) throw Error('Shopify section response was incomplete');
        if (id !== seq) return;
        oldInfo.replaceWith(newInfo);
        activateForm();
        const qty = root.querySelector('[data-hbp3-qty]');
        if (qty) {qty.value = savedQty; validQty(qty);}
        all(root, '.hbp3-details').forEach((node, i) => {if (expanded[i]) node.open = true;});
        const flags = root.querySelector('[data-hbp3-badges]');
        const newFlags = incoming.querySelector('[data-hbp3-badges]');
        if (flags && newFlags) flags.replaceWith(newFlags);
        if (newInfo.dataset.featuredId) showSlide(newInfo.dataset.featuredId);
        const variantId = newInfo.dataset.variantId;
        if (variantId) {
          url.searchParams.delete('option_values');
          url.searchParams.set('variant', variantId);
        }
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
        updateMobile();
        observeMobileButton();
        if (changed?.dataset.hbp3OptionId) {
          all(root, '[data-hbp3-option-id]').find((node) => node.dataset.hbp3OptionId === changed.dataset.hbp3OptionId)?.focus({preventScroll:true});
        }
      } catch (error) {
        if (error.name !== 'AbortError') window.location.assign(url.toString());
      } finally {
        if (id === seq) root.removeAttribute('aria-busy');
      }
    }

    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-hbp3-qty]')) {validQty(event.target); return;}
      if (event.target.matches('[data-hbp3-option-id], [data-hbp3-plan]')) updateOptions(event.target);
    });

    root.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!form.matches(formSelector)) return;
      const submit = form.querySelector('[data-hbp3-submit]');
      // Leave Shopify's accelerated checkout buttons to Shopify.
      if (event.submitter && event.submitter !== submit) return;
      if (!submit || submit.disabled || sending) {event.preventDefault(); return;}
      event.preventDefault();
      const qty = form.querySelector('[data-hbp3-qty]');
      if (qty) validQty(qty);
      sending = true;
      submit.disabled = true;
      const label = submit.querySelector('[data-hbp3-submit-text]');
      if (label) label.textContent = 'ADDING…';
      updateMobile();
      try {
        const nativeCart = window.HoneybearNativeCart;
        if (!nativeCart) throw new Error('Shopify cart integration did not load.');
        await nativeCart.add(form);
        if (label) label.textContent = 'ADDED TO BAG';
      } catch (error) {
        flash(error.message || 'Could not add item. Try again.');
        if (label) label.textContent = 'TRY AGAIN';
      } finally {
        sending = false;
        const selected = Boolean(root.querySelector('[data-hbp3-id]')?.value);
        const sold = Boolean(root.querySelector('.hbp3-stock.is-out'));
        const liveButton = root.querySelector('[data-hbp3-submit]');
        if (liveButton === submit) {
          submit.disabled = !selected || sold;
          if (label) label.textContent = submit.disabled ? 'SOLD OUT' : 'ADD TO BAG';
        }
        updateMobile();
      }
    });

    activateForm();
    initGallery();
    updateMobile();
    observeMobileButton();
    window.addEventListener('resize', () => {
      if (mobile && window.innerWidth > 720) mobile.hidden = true;
      if (viewport && galleryInitialized && slides()[activeSlide]) {
        viewport.scrollLeft = slides()[activeSlide].offsetLeft;
      }
    }, {passive:true});
  }
  function mount(scope) {
    if (scope.matches?.('[data-hbp3-product]')) init(scope);
    all(scope, '[data-hbp3-product]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document));
  else mount(document);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
